/**
 * @fileoverview Componente responsável por encaminhar mensagens para outros tickets ou contatos.
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  TextField,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Divider,
  useTheme,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useQueues } from "../../context/Queues/QueuesContext";

import { Search, X, Send, ArrowLeft } from "lucide-react";
const ForwardMessageModal = ({ open, onClose, messages = [] }) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const { queues } = useQueues();
  const [selectedQueue, setSelectedQueue] = useState(null);

  useEffect(() => {
    if (open) {
      fetchContacts();
      setSelectedContacts([]);
      setSelectedQueue(null);
      setSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        fetchContacts();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: {
          searchParam: searchTerm,
          companyId: user.companyId
        }
      });
      
      // Remove duplicatas baseado no número do contato e nome
      const uniqueContacts = data.contacts?.filter((contact, index, self) => 
        index === self.findIndex(c => 
          c.number === contact.number && 
          c.name === contact.name
        )
      ) || [];
      
      setContacts(uniqueContacts);
    } catch (err) {
      console.error("❌ Error fetching contacts:", err);
      toastError(err);
    }
    setLoading(false);
  };

  const handleContactSelect = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleForwardMessage = async () => {
    if (selectedContacts.length === 0) {
      toastError("Selecione pelo menos um contato para encaminhar");
      return;
    }

    setSending(true);
    try {
      const forwardPromises = [];
      let lastTicketId = null;

      selectedContacts.forEach((contactId) => {
        messages.forEach((message) => {
          forwardPromises.push(
            api.post("/messages/forward", {
              contactId: contactId,
              ticketId: message.ticketId,
              messageId: message.id,
              queueId: 1  // Use uma fila padrão ou null
            })
          );
        });
      });

      const results = await Promise.all(forwardPromises);

      // Pegar o ticketId do último resultado
      if (results.length > 0 && results[results.length - 1].data?.ticketId) {
        lastTicketId = results[results.length - 1].data.ticketId;
      }

      const successMessage = `${messages.length} mensagem(ns) encaminhada(s) para ${selectedContacts.length} contato(s)`;
      toast.success(successMessage);

      setSending(false);
      onClose();

      // Redirecionar para o último ticket encaminhado
      if (lastTicketId) {
        history.push(`/tickets/${lastTicketId}`);
      }
    } catch (err) {
      toastError(err);
      setSending(false);
    }
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
          maxHeight: '80vh'
        }
      }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        alignItems="center" 
        p={2} 
        borderBottom={`1px solid ${theme.palette.divider}`}
        style={{ 
          backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[900] : "#f8f9fa",
          color: theme.palette.text.primary
        }}
      >
        <IconButton onClick={onClose} size="small" style={{ color: theme.palette.text.primary }}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h6" style={{ flexGrow: 1, marginLeft: 8, color: theme.palette.text.primary }}>
          Encaminhar mensagem
        </Typography>
        <IconButton onClick={onClose} size="small" style={{ color: theme.palette.text.primary }}>
          <X />
        </IconButton>
      </Box>

          <DialogContent style={{ padding: 0 }}>
            {/* Search and Queue Selection */}
            <Box p={2} style={{ backgroundColor: theme.palette.background.paper }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    placeholder="Pesquisar nome ou número"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search style={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                      style: {
                        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5",
                        borderRadius: 8
                      }
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>

        <Divider />

        {/* Contacts List */}
        <Box style={{ 
          maxHeight: 300, 
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper
        }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List style={{ padding: 0 }}>
              {contacts.map((contact, index) => (
                <React.Fragment key={contact.id}>
                  <ListItem
                    button
                    onClick={() => handleContactSelect(contact.id)}
                    style={{
                      paddingLeft: 16,
                      paddingRight: 16,
                      backgroundColor: theme.palette.background.paper
                    }}
                  >
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      color="primary"
                      size="small"
                    />
                    <ListItemAvatar>
                      <Avatar
                        src={contact.profilePicUrl}
                        style={{
                          width: 40,
                          height: 40,
                          backgroundColor: "#25d366"
                        }}
                      >
                        {contact.name ? contact.name.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                          {contact.name || contact.number}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          {contact.number}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < contacts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      {selectedContacts.length > 0 && (
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          p={2}
          borderTop={`1px solid ${theme.palette.divider}`}
          style={{ 
            backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[900] : "#f8f9fa"
          }}
        >
          <Box>
            <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
              {selectedContacts.length} contato(s) selecionado(s)
            </Typography>
            {selectedQueue && (
              <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
                Fila: {selectedQueue.name}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <IconButton 
              onClick={onClose}
              size="small"
              style={{ color: theme.palette.text.secondary }}
            >
              <X />
            </IconButton>
            <IconButton
              onClick={handleForwardMessage}
              disabled={sending}
              style={{ 
                backgroundColor: "#25d366",
                color: "white",
                width: 40,
                height: 40
              }}
            >
              {sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
            </IconButton>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ForwardMessageModal;